import React, { useEffect, useState, useRef } from 'react';
import './AdaptiveModal.scss';

interface AdaptiveModalProps {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const AdaptiveModal: React.FC<AdaptiveModalProps> = ({
  isOpen,
  toggle,
  title,
  children,
  footer,
  size = 'md',
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Media query hook to detect mobile
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    // Initial check
    handleMediaChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  // ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        toggle();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, toggle]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset drag offset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDragOffset(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Drag handlers for mobile bottom sheet
  const handleDragStart = (clientY: number) => {
    if (!isMobile) return;
    setIsDragging(true);
    setDragStartY(clientY);
  };

  const handleDragMove = (clientY: number) => {
    if (!isMobile || !isDragging) return;

    const offset = clientY - dragStartY;
    // Only allow dragging down (positive offset)
    if (offset > 0) {
      setDragOffset(offset);
    }
  };

  const handleDragEnd = () => {
    if (!isMobile || !isDragging) return;

    setIsDragging(false);

    // If dragged more than 100px, close the modal
    if (dragOffset > 100) {
      toggle();
    }

    // Reset offset
    setDragOffset(0);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse events (for testing on desktop)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  if (!isOpen) return null;

  // Mobile: Bottom Sheet
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div className="adaptive-modal-backdrop" onClick={toggle} />

        {/* Bottom Sheet */}
        <div
          ref={sheetRef}
          className={`adaptive-modal-bottom-sheet ${isDragging ? 'dragging' : ''}`}
          style={{
            transform: `translateY(${dragOffset}px)`,
          }}
          onMouseMove={isDragging ? handleMouseMove : undefined}
          onMouseUp={isDragging ? handleMouseUp : undefined}
          onMouseLeave={isDragging ? handleMouseUp : undefined}
        >
          {/* Drag Handle */}
          <div
            className="adaptive-modal-handle"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          />

          {/* Header */}
          <div className="adaptive-modal-header">
            <h5 className="adaptive-modal-title">{title}</h5>
            <button
              type="button"
              className="adaptive-modal-close"
              onClick={toggle}
              aria-label="Kapat"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* Body */}
          <div className="adaptive-modal-body">{children}</div>

          {/* Footer */}
          {footer && <div className="adaptive-modal-footer">{footer}</div>}
        </div>
      </>
    );
  }

  // Desktop: Side Modal (same as SideModal component)
  return (
    <>
      {/* Backdrop */}
      <div className="adaptive-modal-backdrop" onClick={toggle} />

      {/* Side Panel */}
      <div className={`adaptive-modal-side-panel adaptive-modal-${size}`}>
        {/* Header */}
        <div className="adaptive-modal-header">
          <h5 className="adaptive-modal-title">{title}</h5>
          <button
            type="button"
            className="adaptive-modal-close"
            onClick={toggle}
            aria-label="Kapat"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Body */}
        <div className="adaptive-modal-body">{children}</div>

        {/* Footer */}
        {footer && <div className="adaptive-modal-footer">{footer}</div>}
      </div>
    </>
  );
};

export default AdaptiveModal;
